import { AlertCircle, Check, ChevronDown, ChevronLeft, ChevronRight, Heart, PackageCheck, Search, ShoppingCart, SlidersHorizontal, Star, Truck, X, Zap, } from "lucide-react";
import { useEffect, useMemo, useRef, useState, } from "react";
import { useNavigate, } from "react-router-dom";
import { DEAL_TABS, SORT_OPTIONS, mapCatalogProductToCard, formatPrice, type CategoryCardData, type DealSort, type DealTabId, } from "./TodaysDealsTypes";
import "./TodaysDealsPage.css";
import HomeNavbar from "../../globalcomponents/HomeNavbar/HomeNavbar";
import HomeSecondaryNavbar from "../../globalcomponents/HomeSecondaryNavbar/HomeSecondaryNavbar";
import { useCart } from "../../../context/CartContext";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { fetchCategoryTree, fetchDealsBrowse } from "../../../Redux/ecomCatalog/slice";
import type { EcomDealsQueryParams } from "../../../Redux/ecomCatalog/types";
import MobileDetailNavbar from "../../globalcomponents/mobile/MobileDetailNavbar/MobileDetailNavbar";

const PAGE_SIZE = 24;
const SEARCH_DEBOUNCE_MS = 300;

type MobileFilterSection =
    | "sort"
    | "department"
    | "delivery"
    | "price"
    | "discount"
    | "rating"
    | "brands";

const todayDealsMobileFilterSections: { id: MobileFilterSection; label: string }[] = [
    { id: "sort",       label: "Sort" },
    { id: "department", label: "Department" },
    { id: "delivery",   label: "Delivery" },
    { id: "price",      label: "Price" },
    { id: "discount",   label: "Discount" },
    { id: "rating",     label: "Customer Reviews" },
    { id: "brands",     label: "Brands" },
];

const TodaysDealsPage = () => {
    const navigate = useNavigate();
    const { addToCart, isProductInCart, cartCount } = useCart();
    const dispatch = useAppDispatch();
    const { deals, loading, error, tree } = useAppSelector((s) => s.ecomCatalog);
    const tabsRef = useRef<HTMLDivElement | null>(null);

    // ─── UI-only state ─────────────────────────────────────────────────────────
    const [browsingHistoryOpen, setBrowsingHistoryOpen,] = useState(false);
    const [wishlist, setWishlist,] = useState<string[]>([]);
    const [selectedColors, setSelectedColors,] = useState<Record<string, number>>({});

    // ─── Server-side query state ───────────────────────────────────────────────
    const [activeTab, setActiveTab,] = useState<DealTabId>("todayDeals");
    const [search, setSearch,] = useState("");
    const [debouncedSearch, setDebouncedSearch,] = useState("");
    const [superCategory, setSuperCategory,] = useState<string | undefined>(undefined);
    const [brandIds, setBrandIds,] = useState<string[]>([]);
    const [minPrice, setMinPrice,] = useState<number | undefined>(undefined);
    const [maxPrice, setMaxPrice,] = useState<number | undefined>(undefined);
    const [priceDraft, setPriceDraft,] = useState<{ min: string; max: string }>({ min: "", max: "" });
    const [minDiscount, setMinDiscount,] = useState<number | undefined>(undefined);
    const [minRating, setMinRating,] = useState<number | undefined>(undefined);
    const [freeDelivery, setFreeDelivery,] = useState(false);
    const [fastDelivery, setFastDelivery,] = useState(false);
    const [nextDayDelivery, setNextDayDelivery,] = useState(false);
    const [sort, setSort,] = useState<DealSort>("discount");
    const [page, setPage,] = useState(1);

    // ─── Mobile filter UI state ────────────────────────────────────────────────
    const [mobileFilterOpen, setMobileFilterOpen,] = useState(false);
    const [mobileFilterSection, setMobileFilterSection,] = useState<MobileFilterSection>("sort");
    const [mobileFilterSearch, setMobileFilterSearch,] = useState("");

    // Debounce search → q (deals endpoint is rate-limited)
    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(t);
    }, [search]);

    // Reset to page 1 whenever any filter/tab/sort changes
    useEffect(() => {
        setPage(1);
    }, [activeTab, debouncedSearch, superCategory, brandIds, minPrice, maxPrice, minDiscount, minRating, freeDelivery, fastDelivery, nextDayDelivery, sort]);

    // ─── Build params (mirrors DealsPage.fetchData) ────────────────────────────
    const params: EcomDealsQueryParams = useMemo(() => {
        const dealType = DEAL_TABS.find((t) => t.id === activeTab)?.dealType;
        const p: EcomDealsQueryParams = { page, limit: PAGE_SIZE, sort, withFilters: "true" };
        if (dealType) p.dealType = dealType;
        if (debouncedSearch) p.q = debouncedSearch;
        if (superCategory) p.superCategory = superCategory;
        if (brandIds.length) p.brandId = brandIds;
        if (minPrice !== undefined) p.minPrice = minPrice;
        if (maxPrice !== undefined) p.maxPrice = maxPrice;
        if (minDiscount !== undefined) p.minDiscount = minDiscount;
        if (minRating !== undefined) p.minRating = minRating;
        if (freeDelivery) p.freeDelivery = "true";
        if (fastDelivery) p.fastDelivery = "true";
        if (nextDayDelivery) p.nextDayDelivery = "true";
        return p;
    }, [activeTab, page, sort, debouncedSearch, superCategory, brandIds, minPrice, maxPrice, minDiscount, minRating, freeDelivery, fastDelivery, nextDayDelivery]);

    useEffect(() => {
        dispatch(fetchDealsBrowse(params));
    }, [dispatch, params]);

    useEffect(() => {
        if (tree.length === 0) dispatch(fetchCategoryTree());
    }, [dispatch, tree.length]);

    // ─── Mobile filter helpers ────────────────────────────────────────────────
    useEffect(() => {
        if (!mobileFilterOpen) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setMobileFilterOpen(false);
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [mobileFilterOpen]);

    const openMobileFilter = (section: MobileFilterSection = "sort") => {
        setMobileFilterSection(section);
        setMobileFilterSearch("");
        setMobileFilterOpen(true);
    };

    const mobileFilterMatchesSearch = (label: string) => {
        const query = mobileFilterSearch.trim().toLowerCase();
        if (!query) return true;
        return label.toLowerCase().includes(query);
    };

    const getMobileFilterCount = (section: MobileFilterSection): number => {
        if (section === "sort")       return sort !== "discount" ? 1 : 0;
        if (section === "department") return superCategory !== undefined ? 1 : 0;
        if (section === "delivery")   return (freeDelivery ? 1 : 0) + (fastDelivery ? 1 : 0) + (nextDayDelivery ? 1 : 0);
        if (section === "price")      return (minPrice !== undefined || maxPrice !== undefined) ? 1 : 0;
        if (section === "discount")   return minDiscount !== undefined ? 1 : 0;
        if (section === "rating")     return minRating !== undefined ? 1 : 0;
        return brandIds.length; // brands
    };

    const activeMobileFilterCount = todayDealsMobileFilterSections.reduce(
        (total, section) => total + getMobileFilterCount(section.id),
        0
    );

    const renderMobileFilterOptions = () => {
        // ── Sort ──────────────────────────────────────────────────────────────
        if (mobileFilterSection === "sort") {
            return (
                <div className="todays-deals-mobile-sheet-options">
                    {SORT_OPTIONS
                        .filter((option) => mobileFilterMatchesSearch(option.label))
                        .map((option) => (
                            <label
                                key={option.value}
                                className={`todays-deals-mobile-sheet-option ${sort === option.value ? "active" : ""}`}
                            >
                                <input
                                    type="radio"
                                    name="today-mobile-sort"
                                    checked={sort === option.value}
                                    onChange={() => setSort(option.value)}
                                />
                                <span>{option.label}</span>
                                {sort === option.value && <Check size={16} strokeWidth={2.4} />}
                            </label>
                        ))}
                </div>
            );
        }

        // ── Department ────────────────────────────────────────────────────────
        if (mobileFilterSection === "department") {
            const allOption = { name: "All Departments", count: undefined as number | undefined };
            return (
                <div className="todays-deals-mobile-sheet-options">
                    {[allOption, ...tree]
                        .filter((node) => mobileFilterMatchesSearch(node.name))
                        .map((node) => {
                            const isAll = node.name === "All Departments";
                            const checked = isAll ? superCategory === undefined : superCategory === node.name;
                            return (
                                <label
                                    key={node.name}
                                    className={`todays-deals-mobile-sheet-option ${checked ? "active" : ""}`}
                                >
                                    <input
                                        type="radio"
                                        name="today-mobile-department"
                                        checked={checked}
                                        onChange={() => setSuperCategory(isAll ? undefined : node.name)}
                                    />
                                    <span>
                                        {node.name}
                                        {node.count !== undefined && <> ({node.count})</>}
                                    </span>
                                    {checked && <Check size={16} strokeWidth={2.4} />}
                                </label>
                            );
                        })}
                </div>
            );
        }

        // ── Delivery ──────────────────────────────────────────────────────────
        if (mobileFilterSection === "delivery") {
            const deliveryOpts = [
                { label: "Free Delivery",     value: freeDelivery,     set: setFreeDelivery },
                { label: "Fast Delivery",     value: fastDelivery,     set: setFastDelivery },
                { label: "Next Day Delivery", value: nextDayDelivery,  set: setNextDayDelivery },
            ];
            return (
                <div className="todays-deals-mobile-sheet-options">
                    {deliveryOpts
                        .filter((opt) => mobileFilterMatchesSearch(opt.label))
                        .map((opt) => (
                            <label
                                key={opt.label}
                                className={`todays-deals-mobile-sheet-option ${opt.value ? "active" : ""}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={opt.value}
                                    onChange={() => opt.set(!opt.value)}
                                />
                                <span>{opt.label}</span>
                                {opt.value && <Check size={16} strokeWidth={2.4} />}
                            </label>
                        ))}
                </div>
            );
        }

        // ── Price ─────────────────────────────────────────────────────────────
        if (mobileFilterSection === "price") {
            return (
                <div className="todays-deals-mobile-filter-price-panel">
                    <div className="todays-deals-price-inputs">
                        <input
                            type="number"
                            inputMode="numeric"
                            placeholder={`₹${filters?.priceRange?.min ?? 0}`}
                            value={priceDraft.min}
                            onChange={(e) => setPriceDraft((d) => ({ ...d, min: e.target.value }))}
                            onBlur={commitPrice}
                            onKeyDown={(e) => e.key === "Enter" && commitPrice()}
                            aria-label="Minimum price"
                        />
                        <span>–</span>
                        <input
                            type="number"
                            inputMode="numeric"
                            placeholder={`₹${filters?.priceRange?.max ?? ""}`}
                            value={priceDraft.max}
                            onChange={(e) => setPriceDraft((d) => ({ ...d, max: e.target.value }))}
                            onBlur={commitPrice}
                            onKeyDown={(e) => e.key === "Enter" && commitPrice()}
                            aria-label="Maximum price"
                        />
                    </div>
                    {(minPrice !== undefined || maxPrice !== undefined) && (
                        <button
                            type="button"
                            className="todays-deals-inline-clear"
                            onClick={() => {
                                setPriceDraft({ min: "", max: "" });
                                setMinPrice(undefined);
                                setMaxPrice(undefined);
                            }}
                        >
                            Clear price
                        </button>
                    )}
                </div>
            );
        }

        // ── Discount ──────────────────────────────────────────────────────────
        if (mobileFilterSection === "discount") {
            const discountBuckets =
                filters?.discountBuckets && filters.discountBuckets.length > 0
                    ? filters.discountBuckets
                    : [
                        { minDiscount: 10, label: "10% off or more", count: undefined as number | undefined },
                        { minDiscount: 20, label: "20% off or more", count: undefined as number | undefined },
                        { minDiscount: 30, label: "30% off or more", count: undefined as number | undefined },
                        { minDiscount: 40, label: "40% off or more", count: undefined as number | undefined },
                        { minDiscount: 50, label: "50% off or more", count: undefined as number | undefined },
                    ];
            return (
                <div className="todays-deals-mobile-sheet-options">
                    {mobileFilterMatchesSearch("Any discount") && (
                        <label className={`todays-deals-mobile-sheet-option ${minDiscount === undefined ? "active" : ""}`}>
                            <input
                                type="radio"
                                name="today-mobile-discount"
                                checked={minDiscount === undefined}
                                onChange={() => setMinDiscount(undefined)}
                            />
                            <span>Any discount</span>
                            {minDiscount === undefined && <Check size={16} strokeWidth={2.4} />}
                        </label>
                    )}
                    {discountBuckets
                        .filter((b) => mobileFilterMatchesSearch(b.label))
                        .map((bucket) => (
                            <label
                                key={bucket.minDiscount}
                                className={`todays-deals-mobile-sheet-option ${minDiscount === bucket.minDiscount ? "active" : ""}`}
                            >
                                <input
                                    type="radio"
                                    name="today-mobile-discount"
                                    checked={minDiscount === bucket.minDiscount}
                                    onChange={() => setMinDiscount(bucket.minDiscount)}
                                />
                                <span>
                                    {bucket.label}
                                    {bucket.count !== undefined && <small> ({bucket.count})</small>}
                                </span>
                                {minDiscount === bucket.minDiscount && <Check size={16} strokeWidth={2.4} />}
                            </label>
                        ))}
                </div>
            );
        }

        // ── Rating ────────────────────────────────────────────────────────────
        if (mobileFilterSection === "rating") {
            const ratingBuckets =
                filters?.ratingBuckets && filters.ratingBuckets.length > 0
                    ? filters.ratingBuckets
                    : [
                        { minRating: 4, label: "4★ & above", count: undefined as number | undefined },
                        { minRating: 3, label: "3★ & above", count: undefined as number | undefined },
                    ];
            return (
                <div className="todays-deals-mobile-sheet-options">
                    {ratingBuckets
                        .filter((b) => mobileFilterMatchesSearch(b.label))
                        .map((bucket) => (
                            <button
                                type="button"
                                key={bucket.minRating}
                                className={`todays-deals-mobile-rating-option ${minRating === bucket.minRating ? "active" : ""}`}
                                onClick={() => setMinRating(minRating === bucket.minRating ? undefined : bucket.minRating)}
                            >
                                <span className="todays-deals-mobile-rating-stars">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <Star
                                            key={star}
                                            size={14}
                                            strokeWidth={1.8}
                                            fill={star <= bucket.minRating ? "currentColor" : "none"}
                                        />
                                    ))}
                                </span>
                                <strong>
                                    {bucket.label}
                                    {bucket.count !== undefined && <small> ({bucket.count})</small>}
                                </strong>
                                {minRating === bucket.minRating && <Check size={16} strokeWidth={2.4} />}
                            </button>
                        ))}
                </div>
            );
        }

        // ── Brands ────────────────────────────────────────────────────────────
        const brandList = filters?.brands ?? [];
        return (
            <div className="todays-deals-mobile-sheet-options">
                {brandList
                    .filter((brand:any) => mobileFilterMatchesSearch(brand.name))
                    .map((brand:any) => (
                        <label
                            key={brand._id}
                            className={`todays-deals-mobile-sheet-option ${brandIds.includes(brand._id) ? "active" : ""}`}
                        >
                            <input
                                type="checkbox"
                                checked={brandIds.includes(brand._id)}
                                onChange={() => toggleBrand(brand._id)}
                            />
                            <span>
                                {brand.name}
                                {brand.count !== undefined && <small> ({brand.count})</small>}
                            </span>
                        </label>
                    ))}
            </div>
        );
    };

    // ─── Derived ───────────────────────────────────────────────────────────────
    const cards: CategoryCardData[] = useMemo(
        () => deals.products.map(mapCatalogProductToCard),
        [deals.products],
    );
    const filters = deals.filters;
    const pagination = deals.pagination;
    const activeTabLabel = DEAL_TABS.find((t) => t.id === activeTab)?.label ?? "Deals";

    // ─── Handlers ──────────────────────────────────────────────────────────────
    const resetFilters = () => {
        setSearch("");
        setDebouncedSearch("");
        setSuperCategory(undefined);
        setBrandIds([]);
        setMinPrice(undefined);
        setMaxPrice(undefined);
        setPriceDraft({ min: "", max: "" });
        setMinDiscount(undefined);
        setMinRating(undefined);
        setFreeDelivery(false);
        setFastDelivery(false);
        setNextDayDelivery(false);
        setSort("discount");
    };
    const clearFilters = () => {
        resetFilters();
        setActiveTab("todayDeals");
    };
    const handleTabChange = (tab: DealTabId) => {
        setActiveTab(tab);
        resetFilters();
    };
    const toggleBrand = (id: string) =>
        setBrandIds((prev) => (prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id]));
    const toggleWishlist = (id: string) =>
        setWishlist((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
    const selectColor = (productId: string, colorIndex: number) =>
        setSelectedColors((prev) => ({ ...prev, [productId]: colorIndex }));
    const commitPrice = () => {
        const min = priceDraft.min === "" ? undefined : Number(priceDraft.min);
        const max = priceDraft.max === "" ? undefined : Number(priceDraft.max);
        setMinPrice(Number.isFinite(min as number) ? min : undefined);
        setMaxPrice(Number.isFinite(max as number) ? max : undefined);
    };
    const scrollTabs = (direction: "left" | "right") => {
        tabsRef.current?.scrollBy({ left: direction === "left" ? -420 : 420, behavior: "smooth" });
    };
    const handleProductClick = (card: CategoryCardData) => {
        navigate(`/DetailPage/${card.id}`);
    };
    const handleAddToCart = (card: CategoryCardData) => {
        if (isProductInCart(card.id)) return;
        const color = card.colors[selectedColors[card.id] ?? 0];
        addToCart({
            productId: card.id,
            title: card.name,
            description: card.description,
            image: card.image,
            price: card.price,
            mrp: card.mrp,
            color,
            variant: color,
        });
    };
    const goToPage = (next: number) => {
        if (next < 1 || next > pagination.totalPages || next === page) return;
        setPage(next);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // Numbered pager — same 5-window logic as DealsPage.renderPagination
    const pageNumbers = useMemo(() => {
        const total = pagination.totalPages || 0;
        if (total <= 1) return [];
        const maxVisible = 5;
        let start = Math.max(1, page - Math.floor(maxVisible / 2));
        const end = Math.min(total, start + maxVisible - 1);
        if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    }, [pagination.totalPages, page]);

    const isInitialLoading = loading && cards.length === 0;

    return (
        <>
            {/* Desktop navbars — hidden on mobile */}
            <div className="todays-deals-desktop-navbar">
                <HomeNavbar variant="home" />
            </div>
            <div className="todays-deals-desktop-secondary-navbar">
                <HomeSecondaryNavbar variant="detail" browsingHistoryOpen={browsingHistoryOpen}
                    onBrowsingHistoryClick={() => setBrowsingHistoryOpen((current) => !current)} />
            </div>

            {/* Mobile navbar — hidden on desktop */}
            <div className="todays-deals-mobile-navbar">
                <MobileDetailNavbar
                    cartCount={cartCount}
                    searchPlaceholder="Search today's deals"
                    fallbackPath="/HomePage"
                />
            </div>

            {/* Sticky mobile tabs bar — hidden on desktop */}
            <div className="todays-deals-mobile-tabs-bar">
                <button
                    type="button"
                    className={`todays-deals-mobile-filter-trigger ${activeMobileFilterCount > 0 ? "active" : ""}`}
                    onClick={() => openMobileFilter("sort")}
                >
                    <SlidersHorizontal size={16} strokeWidth={2} />
                    <span>Sort &amp; Filter</span>
                    {activeMobileFilterCount > 0 && <strong>{activeMobileFilterCount}</strong>}
                </button>
                <div className="todays-deals-mobile-tabs-scroll">
                    {DEAL_TABS.map((tab) => (
                        <button
                            type="button"
                            key={tab.id}
                            className={activeTab === tab.id ? "active" : ""}
                            onClick={() => handleTabChange(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            <main className="todays-deals-page">
                <div className="todays-deals-shell">
                    <div className="todays-deals-breadcrumb">
                        <button type="button" onClick={() => navigate("/HomePage")}>
                            Home
                        </button>
                        <ChevronRight size={15} />
                        <strong> Today's Deals</strong>
                    </div>
                    <section className="todays-deals-hero">
                        <div>
                            <span> LIMITED-TIME SAVINGS </span>
                            <h1> Today's Deals </h1>
                            <p> Discover fresh price drops across fashion, electronics, appliances, grocery and more. </p>
                        </div>
                        <label className="todays-deals-search">
                            <Search size={20} />
                            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search today's deals" />
                        </label>
                    </section>
                    <section className="todays-deals-tabs-wrap">
                        <button type="button" className="todays-deals-tab-arrow" onClick={() => scrollTabs("left")} aria-label="Scroll deal tabs left">
                            <ChevronLeft size={20} />
                        </button>
                        <div className="todays-deals-tabs" ref={tabsRef}>
                            {DEAL_TABS.map((tab) => (
                                <button type="button" key={tab.id} className={activeTab === tab.id ? "active" : ""} onClick={() => handleTabChange(tab.id)}>
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                        <button type="button" className="todays-deals-tab-arrow" onClick={() => scrollTabs("right")} aria-label="Scroll deal tabs right">
                            <ChevronRight size={20} />
                        </button>
                    </section>
                    <div className="todays-deals-content">
                        <aside className="todays-deals-filters">
                            <div className="todays-deals-filter-head">
                                <div>
                                    <SlidersHorizontal size={20} />
                                    <strong> Filters </strong>
                                </div>
                                <button type="button" onClick={clearFilters}> Clear all </button>
                            </div>

                            {/* Department — live superCategories from the category tree */}
                            <div className="todays-deals-filter-section department-section">
                                <h2> Department </h2>
                                <div className="todays-deals-department-list">
                                    <label className="todays-deals-filter-option">
                                        <input type="radio" name="today-department" checked={superCategory === undefined} onChange={() => setSuperCategory(undefined)} />
                                        <span> All Departments </span>
                                    </label>
                                    {tree.map((node) => (
                                        <label key={node.name} className="todays-deals-filter-option">
                                            <input type="radio" name="today-department" checked={superCategory === node.name} onChange={() => setSuperCategory(node.name)} />
                                            <span> {node.name} <small>({node.count})</small></span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Delivery */}
                            <div className="todays-deals-filter-section">
                                <h2> Delivery </h2>
                                {[
                                    { label: "Free Delivery", value: freeDelivery, set: setFreeDelivery },
                                    { label: "Fast Delivery", value: fastDelivery, set: setFastDelivery },
                                    { label: "Next Day Delivery", value: nextDayDelivery, set: setNextDayDelivery },
                                ].map((opt) => (
                                    <label className="todays-deals-filter-option" key={opt.label}>
                                        <input type="checkbox" checked={opt.value} onChange={() => opt.set(!opt.value)} />
                                        <span> {opt.label} </span>
                                    </label>
                                ))}
                            </div>

                            {/* Price — bounds from API facet; committed on blur/Enter to respect rate limit */}
                            <div className="todays-deals-filter-section">
                                <h2> Price </h2>
                                <div className="todays-deals-price-inputs">
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        placeholder={`₹${filters?.priceRange?.min ?? 0}`}
                                        value={priceDraft.min}
                                        onChange={(e) => setPriceDraft((d) => ({ ...d, min: e.target.value }))}
                                        onBlur={commitPrice}
                                        onKeyDown={(e) => e.key === "Enter" && commitPrice()}
                                        aria-label="Minimum price"
                                    />
                                    <span>–</span>
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        placeholder={`₹${filters?.priceRange?.max ?? ""}`}
                                        value={priceDraft.max}
                                        onChange={(e) => setPriceDraft((d) => ({ ...d, max: e.target.value }))}
                                        onBlur={commitPrice}
                                        onKeyDown={(e) => e.key === "Enter" && commitPrice()}
                                        aria-label="Maximum price"
                                    />
                                </div>
                                {(minPrice !== undefined || maxPrice !== undefined) && (
                                    <button type="button" className="todays-deals-inline-clear" onClick={() => { setPriceDraft({ min: "", max: "" }); setMinPrice(undefined); setMaxPrice(undefined); }}>
                                        Clear price
                                    </button>
                                )}
                            </div>

                            {/* Discount — API discount buckets */}
                            {filters && filters.discountBuckets.length > 0 && (
                                <div className="todays-deals-filter-section">
                                    <h2> Discount </h2>
                                    <label className="todays-deals-filter-option">
                                        <input type="radio" name="today-discount" checked={minDiscount === undefined} onChange={() => setMinDiscount(undefined)} />
                                        <span> Any discount </span>
                                    </label>
                                    {filters.discountBuckets.map((bucket) => (
                                        <label className="todays-deals-filter-option" key={bucket.minDiscount}>
                                            <input type="radio" name="today-discount" checked={minDiscount === bucket.minDiscount} onChange={() => setMinDiscount(bucket.minDiscount)} />
                                            <span> {bucket.label} <small>({bucket.count})</small></span>
                                        </label>
                                    ))}
                                </div>
                            )}

                            {/* Customer Reviews — API rating buckets, fallback presets like DealsFilter */}
                            <div className="todays-deals-filter-section">
                                <h2> Customer Reviews</h2>
                                {(filters?.ratingBuckets && filters.ratingBuckets.length > 0
                                    ? filters.ratingBuckets
                                    : [
                                        { minRating: 4, label: "4★ & above", count: undefined as number | undefined },
                                        { minRating: 3, label: "3★ & above", count: undefined as number | undefined },
                                    ]
                                ).map((bucket) => (
                                    <button type="button" className={`todays-deals-rating-filter ${minRating === bucket.minRating ? "active" : ""}`}
                                        key={bucket.minRating}
                                        onClick={() => setMinRating(minRating === bucket.minRating ? undefined : bucket.minRating)}>
                                        <span className="todays-deals-filter-stars">
                                            {[1, 2, 3, 4, 5,].map((star) => (<Star key={star} size={14} fill={star <= bucket.minRating ? "currentColor" : "none"} />))}
                                        </span>
                                        <span> {bucket.label}{bucket.count !== undefined && <small> ({bucket.count})</small>}</span>
                                    </button>
                                ))}
                            </div>

                            {/* Brands — API brand facet with counts */}
                            {filters && filters.brands.length > 0 && (
                                <div className="todays-deals-filter-section brand-section">
                                    <h2> Brands </h2>
                                    <div className="todays-deals-brand-list">
                                        {filters.brands.map((brand) => (
                                            <label className="todays-deals-filter-option" key={brand._id}>
                                                <input type="checkbox" checked={brandIds.includes(brand._id)} onChange={() => toggleBrand(brand._id)} />
                                                <span> {brand.name} <small>({brand.count})</small></span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </aside>

                        <section className="todays-deals-results">
                            <div className="todays-deals-results-head">
                                <div>
                                    <span> DEALS FOR YOU </span>
                                    <h2> {activeTabLabel} </h2>
                                    <p>
                                        {isInitialLoading
                                            ? "Loading deals…"
                                            : `${pagination.total ?? cards.length} deals available`}
                                    </p>
                                </div>
                                <label className="todays-deals-sort">
                                    <span> Sort by </span>
                                    <div>
                                        <select value={sort} onChange={(event) => setSort(event.target.value as DealSort)}>
                                            {SORT_OPTIONS.map((o) => (
                                                <option key={o.value} value={o.value}>{o.label}</option>
                                            ))}
                                        </select>
                                        <ChevronDown size={16} />
                                    </div>
                                </label>
                            </div>

                            {error && cards.length === 0 ? (
                                <div className="todays-deals-empty">
                                    <AlertCircle size={30} />
                                    <h3> Something went wrong </h3>
                                    <p> {error} </p>
                                    <button type="button" onClick={() => dispatch(fetchDealsBrowse(params))}>Try again</button>
                                </div>
                            ) : isInitialLoading ? (
                                <div className="todays-deals-product-grid">
                                    {Array.from({ length: 12 }).map((_, i) => (
                                        <div key={i} className="men-product-card todays-deals-skeleton" />
                                    ))}
                                </div>
                            ) : cards.length > 0 ? (
                                <>
                                    <div className={`todays-deals-product-grid ${loading ? "is-refreshing" : ""}`}>
                                        {cards.map((card) => {
                                            const isWishlisted = wishlist.includes(card.id);
                                            const selectedColor = selectedColors[card.id] ?? 0;
                                            const isAdded = isProductInCart(card.id);
                                            const badge = card.isSponsored ? "SPONSORED" : card.dealLabel ? "DEAL" : card.couponLabel ? "COUPON" : null;
                                            const deliveryType = card.nextDayDelivery || card.fastDelivery ? "fast" : card.freeDelivery ? "delivery" : "stock";
                                            const deliveryLabel = card.stockStatus === "out_of_stock"
                                                ? "Out of stock"
                                                : card.nextDayDelivery ? "Next Day Delivery"
                                                    : card.fastDelivery ? "Fast Delivery Available"
                                                        : card.freeDelivery ? "Free Delivery"
                                                            : card.stockStatus === "low_stock" ? "Only Few Left in Stock" : "In Stock";
                                            return (
                                                <article className="men-product-card"
                                                    key={card.id} role="button" tabIndex={0}
                                                    onClick={(event) => {
                                                        const target = event.target as HTMLElement;
                                                        if (target.closest("button")) { return; }
                                                        handleProductClick(card);
                                                    }}
                                                    onKeyDown={(event) => {
                                                        if (event.key === "Enter" || event.key === " ") {
                                                            event.preventDefault();
                                                            handleProductClick(card);
                                                        }
                                                    }}>
                                                    <div className="men-product-image-wrapper">
                                                        {badge && <span className="men-product-badge"> {badge}</span>}
                                                        <button type="button" className={`men-product-wishlist ${isWishlisted ? "active" : ""}`}
                                                            onClick={() => toggleWishlist(card.id)}
                                                            aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}>
                                                            <Heart size={18} strokeWidth={2} fill={isWishlisted ? "currentColor" : "none"} />
                                                        </button>
                                                        <img src={card.image} alt={card.name} className="men-product-image" loading="lazy"
                                                            onError={(e) => { (e.currentTarget as HTMLImageElement).src = "/assets/images/logos/salarlogo.png"; }} />
                                                    </div>
                                                    <div className="men-product-info">
                                                        {card.colors.length > 0 && (
                                                            <div className="men-product-colors">
                                                                <div className="men-product-color-list">
                                                                    {card.colors.slice(0, 5).map((color, index) => (
                                                                        <button type="button" key={`${color}-${index}`} className={`men-product-color ${selectedColor === index ? "active" : ""}`} title={color} aria-label={color} onClick={() => selectColor(card.id, index)}>
                                                                            <span style={{ backgroundColor: color, }} />
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                                {card.colors.length > 5 && (
                                                                    <button type="button" className="men-product-more-colors" onClick={() => handleProductClick(card)}>
                                                                        {`+${card.colors.length - 5} more`}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                        <h3>{card.name}</h3>
                                                        <p className="men-product-description">{card.description || card.brandName}</p>
                                                        <div className="men-product-rating">
                                                            {card.rating > 0 ? (
                                                                <>
                                                                    <strong>{card.rating.toFixed(1)}</strong>
                                                                    <span className="men-product-stars">
                                                                        {[1, 2, 3, 4, 5].map((star) => (
                                                                            <Star key={star} size={13} strokeWidth={1.8}
                                                                                fill={star <= Math.round(card.rating) ? "currentColor" : "none"} />))}
                                                                    </span>
                                                                    <span className="men-product-review-count">({card.reviewCount}) </span>
                                                                </>
                                                            ) : (
                                                                <span className="men-product-stars">
                                                                    {[1, 2, 3, 4, 5].map((star) => (<Star key={star} size={13} strokeWidth={1.8} fill="none" />))}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {(card.dealLabel || card.couponLabel) && (
                                                            <div className={`men-product-deal ${card.dealLabel ? "limited" : "special"}`}>
                                                                <span className="men-product-deal-dot" />
                                                                <strong>{card.dealLabel ? `Deal: ${card.dealLabel}` : `Coupon: ${card.couponLabel}`}</strong>
                                                            </div>
                                                        )}
                                                        <div className="men-product-pricing">
                                                            <div className="men-product-price-main">
                                                                <strong><sup>₹</sup>{formatPrice(card.price)}</strong>
                                                                {card.discountPercent > 0 && (
                                                                    <span className="men-product-discount"> {card.discountPercent}% OFF</span>
                                                                )}
                                                            </div>
                                                            {card.mrp > card.price && (
                                                                <div className="men-product-price-secondary">
                                                                    <span> MRP</span>
                                                                    <del>₹{formatPrice(card.mrp)} </del>
                                                                    <span className="men-product-saving">
                                                                        Save ₹{formatPrice(card.mrp - card.price)}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className={`men-product-delivery ${deliveryType}`}>
                                                            {deliveryType === "delivery" && (<Truck size={15} strokeWidth={1.9} />)}
                                                            {deliveryType === "fast" && (<Zap size={15} strokeWidth={2} />)}
                                                            {deliveryType === "stock" && (<PackageCheck size={15} strokeWidth={1.9} />)}
                                                            <span>{deliveryLabel}</span>
                                                        </div>
                                                        <button type="button" className={`men-product-options ${isAdded ? "added" : ""}`} onClick={() => handleAddToCart(card)} disabled={card.stockStatus === "out_of_stock"}>
                                                            {isAdded ? (
                                                                <> <Check size={16} strokeWidth={2.4} /> Added to Cart </>
                                                            ) : (
                                                                <> <ShoppingCart size={16} />Add to Cart </>
                                                            )}
                                                        </button>
                                                    </div>
                                                </article>
                                            );
                                        })}
                                    </div>

                                    {pageNumbers.length > 0 && (
                                        <nav className="todays-deals-pagination" aria-label="Deals pagination">
                                            <button type="button" onClick={() => goToPage(page - 1)} disabled={page <= 1} aria-label="Previous page">
                                                <ChevronLeft size={16} />
                                            </button>
                                            {pageNumbers[0] > 1 && (
                                                <>
                                                    <button type="button" onClick={() => goToPage(1)}>1</button>
                                                    {pageNumbers[0] > 2 && <span>…</span>}
                                                </>
                                            )}
                                            {pageNumbers.map((n) => (
                                                <button type="button" key={n} className={n === page ? "active" : ""} onClick={() => goToPage(n)} aria-current={n === page ? "page" : undefined}>
                                                    {n}
                                                </button>
                                            ))}
                                            {pageNumbers[pageNumbers.length - 1] < pagination.totalPages && (
                                                <>
                                                    {pageNumbers[pageNumbers.length - 1] < pagination.totalPages - 1 && <span>…</span>}
                                                    <button type="button" onClick={() => goToPage(pagination.totalPages)}>{pagination.totalPages}</button>
                                                </>
                                            )}
                                            <button type="button" onClick={() => goToPage(page + 1)} disabled={page >= pagination.totalPages} aria-label="Next page">
                                                <ChevronRight size={16} />
                                            </button>
                                        </nav>
                                    )}
                                </>
                            ) : (
                                <div className="todays-deals-empty">
                                    <Search size={30} />
                                    <h3> No deals match these filters </h3>
                                    <p> Clear one or more filters to continue browsing today's offers. </p>
                                    <button type="button" onClick={clearFilters}>Clear Filters </button>
                                </div>
                            )}
                        </section>
                    </div>
                </div>
            </main>

            {/* Mobile filter bottom-sheet */}
            {mobileFilterOpen && (
                <div
                    className="todays-deals-mobile-filter-layer"
                    role="presentation"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) setMobileFilterOpen(false);
                    }}
                >
                    <section
                        className="todays-deals-mobile-filter-sheet"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Today's deals filters"
                    >
                        <div className="todays-deals-mobile-filter-handle" />

                        <div className="todays-deals-mobile-filter-header">
                            <div>
                                <span>REFINE DEALS</span>
                                <h2>Filters</h2>
                            </div>
                            <div className="todays-deals-mobile-filter-header-actions">
                                <button
                                    type="button"
                                    className="todays-deals-mobile-clear-all"
                                    onClick={clearFilters}
                                >
                                    Clear All
                                </button>
                                <button
                                    type="button"
                                    className="todays-deals-mobile-filter-close"
                                    onClick={() => setMobileFilterOpen(false)}
                                    aria-label="Close filters"
                                >
                                    <X size={19} strokeWidth={2} />
                                </button>
                            </div>
                        </div>

                        <div className="todays-deals-mobile-filter-content">
                            <nav
                                className="todays-deals-mobile-filter-nav"
                                aria-label="Filter categories"
                            >
                                {todayDealsMobileFilterSections.map((section) => {
                                    const count = getMobileFilterCount(section.id);
                                    return (
                                        <button
                                            type="button"
                                            key={section.id}
                                            className={mobileFilterSection === section.id ? "active" : ""}
                                            onClick={() => {
                                                setMobileFilterSection(section.id);
                                                setMobileFilterSearch("");
                                            }}
                                        >
                                            <span>{section.label}</span>
                                            {count > 0 && <strong>{count}</strong>}
                                        </button>
                                    );
                                })}
                            </nav>

                            <div className="todays-deals-mobile-filter-panel">
                                <div className="todays-deals-mobile-filter-panel-title">
                                    <span>
                                        {todayDealsMobileFilterSections.find((s) => s.id === mobileFilterSection)?.label}
                                    </span>
                                    <strong>{getMobileFilterCount(mobileFilterSection)} selected</strong>
                                </div>

                                <label className="todays-deals-mobile-filter-search">
                                    <Search size={18} strokeWidth={2} />
                                    <input
                                        type="search"
                                        value={mobileFilterSearch}
                                        onChange={(event) => setMobileFilterSearch(event.target.value)}
                                        placeholder={`Search ${
                                            todayDealsMobileFilterSections.find((s) => s.id === mobileFilterSection)?.label ?? "filters"
                                        }`}
                                    />
                                    {mobileFilterSearch && (
                                        <button
                                            type="button"
                                            onClick={() => setMobileFilterSearch("")}
                                            aria-label="Clear filter search"
                                        >
                                            <X size={15} strokeWidth={2} />
                                        </button>
                                    )}
                                </label>

                                <div className="todays-deals-mobile-filter-options-scroll">
                                    {renderMobileFilterOptions()}
                                </div>
                            </div>
                        </div>

                        <div className="todays-deals-mobile-filter-footer">
                            <button
                                type="button"
                                className="todays-deals-mobile-filter-reset"
                                onClick={clearFilters}
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                className="todays-deals-mobile-filter-apply"
                                onClick={() => setMobileFilterOpen(false)}
                            >
                                See {pagination.total ?? cards.length} deals
                            </button>
                        </div>
                    </section>
                </div>
            )}
        </>
    );
};

export default TodaysDealsPage;
